import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Check, X, Zap } from 'lucide-react';
import { toolLabel, intentLabel } from './toolMeta';

/**
 * Expandable per-turn trace timeline shown under an assistant reply:
 * router intent → each tool that ran (ok/fail, rows, timing). Collapsed by
 * default; a lightweight trust/debugging affordance.
 */
export const TurnTrace = ({ intent, steps }) => {
  const [open, setOpen] = useState(false);
  const list = Array.isArray(steps) ? steps : [];
  if (list.length === 0) return null;

  const nTools = list.length;
  return (
    <div className="mt-2 border-t border-slate-100 pt-1.5" data-testid="ai-turn-trace">
      <button
        type="button"
        data-testid="ai-turn-trace-toggle"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-600 transition-colors"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <span>{open ? 'Hide' : 'Show'} trace</span>
        {intent && (
          <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-500">
            {intentLabel(intent)}
          </span>
        )}
        <span className="text-slate-400">· {nTools} tool{nTools > 1 ? 's' : ''}</span>
      </button>

      {open && (
        <ol className="mt-1.5 space-y-1" data-testid="ai-turn-trace-steps">
          {intent && (
            <li className="flex items-center gap-2 text-[11px] text-slate-500">
              <Zap className="h-3 w-3 shrink-0 text-teal-500" />
              <span className="font-medium">Routed as</span>
              <span className="rounded bg-teal-50 px-1.5 py-0.5 text-teal-700">{intentLabel(intent)}</span>
            </li>
          )}
          {list.map((s, i) => (
            <li
              key={i}
              className="flex items-center gap-2 text-[11px]"
              data-testid={`ai-turn-trace-step-${i}`}
            >
              {s.ok === false
                ? <X className="h-3 w-3 shrink-0 text-rose-500" />
                : <Check className="h-3 w-3 shrink-0 text-emerald-500" />}
              <span className="text-slate-600">{toolLabel(s.name)}</span>
              {s.row_count != null && <span className="text-slate-400">· {s.row_count} rows</span>}
              {s.error_code && <span className="text-rose-400">· {s.error_code}</span>}
              {s.elapsed_ms != null && (
                <span className="ml-auto tabular-nums text-slate-300">{s.elapsed_ms} ms</span>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

export default TurnTrace;
